class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (this.isAutocompleteOnly()) {
      event.preventDefault();
      return;
    }

    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a'))
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        if (this.isAutocompleteOnly()) {
          event.preventDefault();
          return;
        }

        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    this.abortController.abort();
    this.abortController = new AbortController();

    const requestedSearchTerm = searchTerm;
    const productTagFilter = this.getProductTagFilter();
    const productVendorFilter = this.getProductVendorFilter();
    const autocompleteOnly = this.isAutocompleteOnly();
    const scopedSearchTerm = this.buildScopedSearchTerm(
      searchTerm,
      productTagFilter,
      productVendorFilter,
    );

    const queryKey = [
      productTagFilter ? `tag-${productTagFilter}` : "global-tag",
      productVendorFilter ? `vendor-${productVendorFilter}` : "global-vendor",
      searchTerm,
    ]
      .join("-")
      .replace(/\s+/g, "-")
      .toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (this.searchTerm === requestedSearchTerm) {
        this.renderSearchResults(this.cachedResults[queryKey]);
      }
      return;
    }

    const searchParams = new URLSearchParams({
      q: scopedSearchTerm,
      "resources[type]":
        productTagFilter || productVendorFilter ? "product" : "product,collection,page,article",
      "resources[options][fields]": "title,product_type,variants.title,variants.sku,vendor,tag",
      section_id: "predictive-search",
    });

    fetch(`${routes.predictive_search_url}?${searchParams.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.searchTerm !== requestedSearchTerm) {
          return;
        }

        let resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        if (productTagFilter || productVendorFilter) {
          resultsMarkup = this.normalizeScopedResultsMarkup(
            resultsMarkup,
            searchTerm,
            scopedSearchTerm,
            productTagFilter,
            productVendorFilter,
            autocompleteOnly,
          );
        }

        resultsMarkup = this.filterCustomProductResults(
          resultsMarkup,
          searchTerm,
        );

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.name === "AbortError" || error?.code === 20) {
          return;
        }

        this.close();
        throw error;
      });
  }

  isAutocompleteOnly() {
    return (
      this.getAttribute("data-autocomplete-only") === "true" ||
      this.input?.form?.dataset?.autocompleteOnly === "true"
    );
  }

  hasAutocompleteResults(resultsMarkup) {
    const doc = new DOMParser().parseFromString(resultsMarkup, "text/html");

    return Boolean(
      doc.querySelector("a[href*='/products/'], .predictive-search__item[href*='/products/']"),
    );
  }

  getProductTagFilter() {
    return (
      this.getAttribute("data-product-tag-filter") ||
      this.input?.dataset?.productTagFilter ||
      ""
    ).trim();
  }

  getProductVendorFilter() {
    return (
      this.getAttribute("data-product-vendor-filter") ||
      this.input?.dataset?.productVendorFilter ||
      ""
    ).trim();
  }

  buildScopedSearchTerm(searchTerm, productTagFilter, productVendorFilter) {
    const queryParts = [searchTerm];

    if (productTagFilter) {
      queryParts.push(`tag:${this.escapeSearchQueryValue(productTagFilter)}`);
    }

    if (productVendorFilter) {
      queryParts.push(`vendor:${this.escapeSearchQueryValue(productVendorFilter)}`);
    }

    return queryParts.join(" ");
  }

  escapeSearchQueryValue(value) {
    return `"${String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
  }

  normalizeScopedResultsMarkup(
    resultsMarkup,
    visibleSearchTerm,
    scopedSearchTerm,
    productTagFilter,
    productVendorFilter,
    autocompleteOnly,
  ) {
    const doc = new DOMParser().parseFromString(resultsMarkup, "text/html");

    if (autocompleteOnly) {
      doc.querySelectorAll("[data-predictive-search-search-for-text]").forEach((element) => {
        const searchItem = element.closest(
          "button.predictive-search__item, li, .predictive-search__item",
        );

        if (searchItem) {
          searchItem.remove();
        }
      });
    }

    doc.querySelectorAll("[data-predictive-search-search-for-text]").forEach((element) => {
      element.textContent = element.textContent.replace(scopedSearchTerm, visibleSearchTerm);
    });

    doc.querySelectorAll("a[href*='/search']").forEach((link) => {
      if (autocompleteOnly) {
        link.removeAttribute("href");
        link.setAttribute("aria-disabled", "true");
        return;
      }

      try {
        const url = new URL(link.getAttribute("href"), window.location.origin);

        if (url.searchParams.get("q") === scopedSearchTerm) {
          url.searchParams.set("q", visibleSearchTerm);
          url.searchParams.set("type", "product");
          url.searchParams.set("options[prefix]", "last");

          if (productTagFilter) {
            url.searchParams.set("filter.p.tag", productTagFilter);
          }

          if (productVendorFilter) {
            url.searchParams.set("filter.p.vendor", productVendorFilter);
          }

          link.setAttribute("href", `${url.pathname}${url.search}`);
        }
      } catch (error) {
        // Keep original href if URL parsing fails.
      }
    });

    return doc.body.innerHTML;
  }

  filterCustomProductResults(resultsMarkup, searchTerm) {
    const doc = new DOMParser().parseFromString(resultsMarkup, "text/html");
    const productItems = Array.from(
      doc.querySelectorAll("[data-custom-search-product]"),
    );

    if (!productItems.length) {
      return resultsMarkup;
    }

    const searchTokens = this.tokenizeSearchValue(searchTerm);

    if (!searchTokens.length) {
      return resultsMarkup;
    }

    // Keep Shopify's native predictive behavior for multi-word queries.
    // The Predictive Search API only returns a limited candidate set,
    // so filtering it client-side can remove valid results that were
    // not included in that initial response.
    if (searchTokens.length > 1) {
      return resultsMarkup;
    }

    productItems.forEach((item) => {
      const productTokens = this.tokenizeSearchValue(
        item.dataset.searchValues || "",
      );

      const matches = searchTokens.every((searchToken) =>
        productTokens.some((productToken) =>
          productToken.startsWith(searchToken),
        ),
      );

      if (!matches) {
        item.remove();
      }
    });

    const productList = doc.querySelector(
      "#predictive-search-results-products-list",
    );

    if (
      productList &&
      !productList.querySelector("[data-custom-search-product]")
    ) {
      productList.closest("div")?.remove();
    }

    return doc.body.innerHTML;
  }

  tokenizeSearchValue(value) {
    return this.normalizeSearchValue(value)
      .split(/[^\p{L}\p{N}]+/u)
      .filter(Boolean);
  }

  normalizeSearchValue(value) {
    return String(value)
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.isAutocompleteOnly() && !this.hasAutocompleteResults(resultsMarkup)) {
      this.predictiveSearchResults.innerHTML = "";
      this.closeResults();
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
